import React, { useState } from 'react';
import ClinicalInputTypeSelector from './ClinicalInputTypeSelector.jsx';
import ClinicalSettingsForm from './ClinicalSettingsForm.jsx';
import ClinicalSidebar from './ClinicalSidebar.jsx';

const NOTES_PLACEHOLDER =
    "Paste clinical notes, lecture summaries, pathology descriptions, or any clinical content here...\n\nExample: 'Patient presents with ST elevations in V1-V4...'";

const FlashClinicalPanel = ({
    inputType,
    onInputTypeChange,
    settings,
    onSettingsChange,
    notesText,
    onNotesTextChange,
    onNotesPaste,
    pastedImagePreviewUrl,
    onRemovePastedImage,
    onGenerateFromNotes,
    isGenerating,
    canGenerate,
    creditsDisplay,
    creditsHint,
    liveCapture,
    uploadCapture,
    imageCapture,
    generatedCards,
    maxCards,
    entitlementsTier,
    onOpenSettings,
}) => {
    const [settingsOpen, setSettingsOpen] = useState(false);

    const showBatchGenerate = inputType === 'notes' || inputType === 'image';
    const showCardCount = inputType === 'notes' || inputType === 'image';

    return (
        <div className="mt-4 sm:mt-8 max-w-6xl mx-auto">
            <div className="mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-white">Create clinical flashcards</h2>
                <p className="mt-1.5 text-sm text-slate-400 max-w-2xl">
                    Transform any clinical content into high-yield study material tuned for your board prep.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-5">
                    <div className="rounded-3xl border border-white/10 bg-slate-900/60 p-4 sm:p-6 shadow-2xl shadow-black/30">
                        <ClinicalInputTypeSelector value={inputType} onChange={onInputTypeChange} />

                        <div className="mt-5">
                            {inputType === 'notes' && (
                                <>
                                    {pastedImagePreviewUrl && (
                                        <div className="mb-3 flex items-center gap-3 rounded-xl border border-white/10 bg-slate-950/40 p-2.5">
                                            <img
                                                src={pastedImagePreviewUrl}
                                                alt="Pasted attachment"
                                                className="h-16 w-16 rounded-lg object-cover border border-white/10"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs font-medium text-slate-200">Image attached</p>
                                                <p className="text-[11px] text-slate-500">Will display on the card — generates 1 card.</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={onRemovePastedImage}
                                                className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs text-slate-400 hover:text-white hover:bg-white/8 transition"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                    <textarea
                                        value={notesText}
                                        onChange={(e) => onNotesTextChange(e.target.value)}
                                        onPaste={onNotesPaste}
                                        placeholder={NOTES_PLACEHOLDER}
                                        rows={7}
                                        className="w-full rounded-2xl bg-slate-950/60 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40 resize-y min-h-[160px]"
                                    />
                                    <p className="mt-1.5 text-[11px] text-slate-500">
                                        Tip: paste a screenshot (e.g. an imaging finding) directly into this box — it'll attach to the card alongside your text.
                                    </p>
                                </>
                            )}
                            {inputType === 'live' && liveCapture && liveCapture}
                            {inputType === 'upload' && uploadCapture && uploadCapture}
                            {inputType === 'image' && imageCapture && imageCapture}
                        </div>

                        {/* Settings — always visible on desktop, collapsible on mobile */}
                        <div className="mt-5">
                            <button
                                type="button"
                                onClick={() => setSettingsOpen(o => !o)}
                                className="lg:hidden w-full flex items-center justify-between rounded-xl bg-white/4 border border-white/8 px-4 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-white/6 transition mb-3"
                            >
                                <span className="font-medium">Settings</span>
                                <svg
                                    className={`h-4 w-4 transition-transform ${settingsOpen ? 'rotate-180' : ''}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <div className={`${settingsOpen ? 'block' : 'hidden'} lg:block`}>
                                <ClinicalSettingsForm
                                    settings={settings}
                                    onChange={onSettingsChange}
                                    showCardCount={showCardCount}
                                    maxCards={maxCards}
                                    entitlementsTier={entitlementsTier}
                                />
                            </div>
                        </div>

                        {showBatchGenerate && (
                            <button
                                type="button"
                                onClick={onGenerateFromNotes}
                                disabled={isGenerating || !canGenerate}
                                className="flash-it-button mt-5 w-full inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-5 text-lg font-semibold ring-1 ring-white/10 disabled:opacity-60 transition min-h-[52px]"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                {isGenerating
                                    ? 'Generating…'
                                    : pastedImagePreviewUrl
                                        ? 'Generate 1 clinical flashcard'
                                        : `Generate ${settings.cardCount} clinical flashcard${settings.cardCount === 1 ? '' : 's'}`}
                            </button>
                        )}
                    </div>

                    {generatedCards}
                </div>

                <div className="hidden lg:block">
                    <ClinicalSidebar settings={settings} creditsDisplay={creditsDisplay} creditsHint={creditsHint} onOpenSettings={onOpenSettings} />
                </div>
            </div>
        </div>
    );
};

export default FlashClinicalPanel;
